// Accountant dashboard panel: how many sales closed each month, with that
// month's total commission dollars. Year is switchable with arrows.
import React, { useMemo, useState } from "react";
import { BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import { formatCurrency } from "../../utils/helper";
import { monthlySales } from "../../utils/salesPerMonth";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function SalesPerMonthPanel({ deals, loaded = true }) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [year, setYear] = useState(currentYear);

  const rows = useMemo(() => monthlySales(deals, year), [deals, year]);
  const yearCount = rows.reduce((n, r) => n + r.count, 0);
  const yearTotal = rows.reduce((n, r) => n + r.total, 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-emerald-100 text-emerald-700 rounded-md inline-flex items-center justify-center">
            <BarChart3 size={14} strokeWidth={2.2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              Sales per month
            </div>
            <div className="text-[11.5px] text-gray-500">
              {loaded
                ? `${yearCount} closed in ${year} · ${formatCurrency(yearTotal)} total commission`
                : "Loading deals…"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-slate-700">
          <button
            onClick={() => setYear((y) => y - 1)}
            className="p-1.5 rounded-lg hover:bg-gray-100"
            aria-label="Previous year"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-[14px] font-semibold w-12 text-center">
            {year}
          </span>
          <button
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= currentYear}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
            aria-label="Next year"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {rows.map((row, i) => {
          const future = year === currentYear && i > currentMonth;
          return (
            <div
              key={MONTH_LABELS[i]}
              className={`bg-gray-50 rounded-xl px-3 py-2.5 text-center ${
                future ? "opacity-50" : ""
              }`}
            >
              <div className="text-[11px] text-gray-400">
                {MONTH_LABELS[i]}
              </div>
              <div className="text-[18px] font-bold text-slate-900 leading-tight">
                {future && row.count === 0 ? "—" : row.count}
              </div>
              <div className="text-[11px] text-gray-500">
                {row.count > 0 ? formatCurrency(row.total) : " "}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
